import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Stage } from '@/components/Stage/Stage';
import {
  createHeySynclyPromptLibraryMutationOptions,
  deleteHeySynclyPromptLibraryMutationOptions,
  updateHeySynclyPromptLibraryMutationOptions,
} from '@/entities/hey-syncly';
import { errorHandler } from '@/error-handler';
import { cn } from '@/lib/cn';
import { Button, Dialog, Tooltip } from '@/shared/ui';
import { toast } from '@/stores/pure/ToastStore';

import PromptLibraryCreateDialogBody from './dialog.create-body';
import { openDeletePromptLibraryDialog } from './dialog.delete';
import PromptLibraryListDialogBody from './dialog.list-body';
import { MAX_CUSTOM_PROMPT_COUNT, type PromptLibraryItem } from './types';

type Props = {
  isLoading: boolean;
  items: PromptLibraryItem[];
  onExitComplete?: () => void;
  onOpenChange: (open: boolean) => void;
  onSelectPrompt: (item: PromptLibraryItem) => void;
  open: boolean;
  workspaceId: string;
};

const headerTextMap = {
  list: {
    title: 'Prompt Library',
    desc: 'Select a saved prompt to instantly fill the input.',
  },
  create: {
    title: 'Create prompt',
    desc: 'Save prompts you use often. Click to instantly fill the input.',
  },
  edit: {
    title: 'Edit prompt',
    desc: 'Update the title or prompt text, then save your changes.',
  },
};

const bodyStageMotion = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -24 },
  transition: { duration: 0.2, ease: 'easeOut' },
} as const;

export const PromptLibraryDialog = ({
  isLoading,
  items,
  onExitComplete,
  onOpenChange,
  onSelectPrompt,
  open,
  workspaceId,
}: Props) => {
  const queryClient = useQueryClient();
  const createMutation = useMutation(
    createHeySynclyPromptLibraryMutationOptions(queryClient),
  );
  const updateMutation = useMutation(
    updateHeySynclyPromptLibraryMutationOptions(queryClient),
  );
  const deleteMutation = useMutation(
    deleteHeySynclyPromptLibraryMutationOptions(queryClient),
  );
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editingPrompt, setEditingPrompt] = useState<PromptLibraryItem | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingLatestSelection, setPendingLatestSelection] = useState<{
    firstPromptId: string | null;
    itemCount: number;
  } | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null);

  const headerText = headerTextMap[mode];
  const isSubmitting =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  useEffect(() => {
    if (!open) {
      setMode('list');
      setEditingPrompt(null);
      setPendingLatestSelection(null);
      setSearchQuery('');
      setSelectedPromptId(items[0]?.id ?? null);
    }
  }, [items, open]);

  useEffect(() => {
    if (items.length === 0) {
      setPendingLatestSelection(null);
      setSelectedPromptId(null);
      return;
    }

    if (pendingLatestSelection) {
      const shouldSelectLatest =
        items.length !== pendingLatestSelection.itemCount ||
        items[0].id !== pendingLatestSelection.firstPromptId;

      if (!shouldSelectLatest) {
        return;
      }

      setSelectedPromptId(items[0].id);
      setPendingLatestSelection(null);
      return;
    }

    if (
      !selectedPromptId ||
      !items.some((item) => item.id === selectedPromptId)
    ) {
      setSelectedPromptId(items[0].id);
    }
  }, [items, pendingLatestSelection, selectedPromptId]);

  const selectedPrompt = useMemo(
    () => items.find((item) => item.id === selectedPromptId) ?? null,
    [items, selectedPromptId],
  );
  const customPromptCount = useMemo(
    () => items.filter((item) => !item.is_default).length,
    [items],
  );
  const canCreatePrompt = customPromptCount < MAX_CUSTOM_PROMPT_COUNT;
  const createPromptDisabledReason =
    'You can save up to 30 prompts. Delete an existing prompt to add a new one.';

  const changeMode = (nextMode: 'list' | 'create' | 'edit') => {
    if (nextMode !== 'edit') {
      setEditingPrompt(null);
    }

    setMode(nextMode);
  };

  const handleCreatePrompt = async ({
    prompt,
    title,
  }: Pick<PromptLibraryItem, 'prompt' | 'title'>) => {
    try {
      await createMutation.mutateAsync({
        workspace_id: workspaceId,
        title,
        prompt,
      });

      setPendingLatestSelection({
        firstPromptId: items[0]?.id ?? null,
        itemCount: items.length,
      });
      changeMode('list');
      toast('Prompt created', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to create prompt'), 'fail');
      throw error;
    }
  };

  const handleEditPrompt = async ({
    prompt,
    title,
  }: Pick<PromptLibraryItem, 'prompt' | 'title'>) => {
    if (!editingPrompt) {
      return;
    }

    try {
      await updateMutation.mutateAsync({
        workspace_id: workspaceId,
        prompt_library_id: editingPrompt.id,
        title,
        prompt,
      });

      changeMode('list');
      toast('Prompt updated', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to edit prompt'), 'fail');
      throw error;
    }
  };

  const handleDeletePrompt = async (item: PromptLibraryItem) => {
    const confirmed = await openDeletePromptLibraryDialog({
      promptTitle: item.title,
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync({
            workspace_id: workspaceId,
            prompt_library_id: item.id,
          });

          if (selectedPromptId === item.id) {
            setSelectedPromptId(null);
          }

          setEditingPrompt(null);
          setMode('list');
          toast('Prompt deleted', 'success');
          return true;
        } catch (error) {
          toast(errorHandler(error, 'Failed to delete prompt'), 'fail');
          return false;
        }
      },
    });

    if (confirmed && selectedPrompt?.id === item.id) {
      setSelectedPromptId(null);
    }
  };

  const handleStartEdit = (item: PromptLibraryItem) => {
    setEditingPrompt(item);
    setMode('edit');
  };

  return (
    <Dialog
      onExitComplete={onExitComplete}
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          changeMode('list');
        }

        onOpenChange(nextOpen);
      }}
    >
      <Dialog.Content size={mode === 'list' ? 'l' : 'm'}>
        <Dialog.Header className="border-b border-border">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Dialog.Title>{headerText.title}</Dialog.Title>
              <Dialog.Description className="mt-1 font-normal text-muted-foreground">
                {headerText.desc}
              </Dialog.Description>
            </div>

            {mode === 'list' ? (
              <Tooltip
                content={createPromptDisabledReason}
                disabled={canCreatePrompt}
              >
                <span>
                  <Button
                    disabled={!canCreatePrompt}
                    onClick={() => changeMode('create')}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <Plus className="size-4" />
                    New prompt
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </div>
        </Dialog.Header>

        <Dialog.Body className="px-0 pt-0 overflow-hidden">
          <div
            className={cn([
              'overflow-hidden h-140',
              {
                'h-157': mode !== 'list',
              },
            ])}
          >
            <Stage>
              <Stage.Scene
                className="h-full"
                status={mode === 'list'}
                options={bodyStageMotion}
              >
                <PromptLibraryListDialogBody
                  isLoading={isLoading}
                  items={items}
                  onDeletePrompt={(item) => {
                    void handleDeletePrompt(item);
                  }}
                  onEditPrompt={handleStartEdit}
                  onSelectPrompt={onSelectPrompt}
                  close={() => onOpenChange(false)}
                  searchQuery={searchQuery}
                  selectedPromptId={selectedPromptId}
                  onSearchQueryChange={setSearchQuery}
                  onSelectedPromptChange={setSelectedPromptId}
                />
              </Stage.Scene>

              <Stage.Scene
                className="h-full"
                status={mode === 'create'}
                options={bodyStageMotion}
              >
                <PromptLibraryCreateDialogBody
                  isSubmitting={isSubmitting}
                  onCancel={() => changeMode('list')}
                  onSubmitPrompt={handleCreatePrompt}
                />
              </Stage.Scene>

              <Stage.Scene
                className="h-full"
                status={mode === 'edit'}
                options={bodyStageMotion}
              >
                {editingPrompt ? (
                  <PromptLibraryCreateDialogBody
                    defaultValues={{
                      title: editingPrompt.title,
                      prompt: editingPrompt.prompt,
                    }}
                    isSubmitting={isSubmitting}
                    onCancel={() => changeMode('list')}
                    onSubmitPrompt={handleEditPrompt}
                    submitLabel="Save changes"
                  />
                ) : null}
              </Stage.Scene>
            </Stage>
          </div>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};
